// src/components/Home.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import RecommendedTrades from "./RecommendedTrades";
import TradeListings from "./TradeListings";
import MarketplaceSkeleton from "./MarketplaceSkeleton";
import AlertToast from "../AlertToast";

import "../App.css";

const safeLower = (v) => String(v ?? "").trim().toLowerCase();

export default function Home({
  searchTerm = "",
  setSearchTerm,
  setFetchedTrades,
}) {
  const { currentUser } = useAuth();
  const isAuthed = !!(currentUser && (currentUser.uid || currentUser._source === "cookie"));

  const location = useLocation();

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [pageLoading, setPageLoading] = useState(true);
  const [prefSet, setPrefSet] = useState(false);

  const prefReqIdRef = useRef(0);
  const readyRef = useRef(false);

  const showToast = useCallback((message, type = "success") => {
    setToast({
      show: true,
      message: String(message || ""),
      type,
    });
  }, []);

  const refreshKey = useMemo(() => location.state?.refresh || null, [location.state]);

  const urlFilters = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const q = String(sp.get("q") || "").trim();
      const category = String(sp.get("category") || "").trim();
      const condRaw = safeLower(sp.get("cond") || "");
      const cond = condRaw === "new" || condRaw === "used" ? condRaw : "all";
      return { q, category, cond };
    } catch {
      return { q: "", category: "", cond: "all" };
    }
  }, [location.search]);

  const effectiveSearchTerm = useMemo(() => {
    const direct = String(searchTerm || "").trim();
    if (direct) return direct;
    return urlFilters.q;
  }, [searchTerm, urlFilters.q]);

  const finishPageLoad = useCallback(() => {
    if (readyRef.current) return;
    readyRef.current = true;
    setPageLoading(false);
  }, []);

  const handleFetchedTrades = useCallback((list) => {
    setFetchedTrades?.(list);
    finishPageLoad();
  }, [setFetchedTrades, finishPageLoad]);

  useEffect(() => {
    readyRef.current = false;
    setPageLoading(true);
  }, [refreshKey, location.pathname, location.search]);

  useEffect(() => {
    let mounted = true;
    const reqId = ++prefReqIdRef.current;

    (async () => {
      try {
        if (!isAuthed) {
          if (mounted) setPrefSet(false);
          return;
        }

        const uid = currentUser?.uid;
        if (!uid) {
          if (mounted) setPrefSet(false);
          return;
        }

        const { db } = await import("../firebase");
        const prefSnap = await db.collection("preferences").doc(uid).get();

        if (!mounted) return;
        if (reqId !== prefReqIdRef.current) return;

        setPrefSet(!!prefSnap.exists);
      } catch {
        if (!mounted) return;
        if (reqId !== prefReqIdRef.current) return;
        setPrefSet(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isAuthed, currentUser?.uid]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      finishPageLoad();
    }, 3500);

    return () => window.clearTimeout(t);
  }, [finishPageLoad, refreshKey, location.pathname, location.search]);

    useEffect(() => {
      if (location.state?.clearFilters && typeof setSearchTerm === "function") {
        setSearchTerm("");
      }
    }, [location.state, setSearchTerm]);

  return (
    <div className="home-wrapper" style={{ position: "relative", minHeight: "100vh" }}>
      {toast.show && (
        <AlertToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "success" })}
        />
      )}

      {pageLoading && (
        <>
          <MarketplaceSkeleton spinnerSrc="/handshake-logo.png" />
          <div className="home-initial-loader" aria-hidden="true">
            <img
              src="/handshake-logo.png"
              alt="Loading"
              className="home-initial-loader__logo"
            />
          </div>
        </>
      )}

      <div
        aria-busy={pageLoading}
        style={{
          opacity: 1,
          pointerEvents: pageLoading ? "none" : "auto",
        }}
      >
        {isAuthed && prefSet ? (
          <RecommendedTrades
            searchTerm={effectiveSearchTerm}
            refreshKey={refreshKey}
            onToast={showToast}
          />
        ) : null}

        <TradeListings
          searchTerm={effectiveSearchTerm}
          publicCategoryKey={urlFilters.category || null}
          conditionFilter={urlFilters.cond}
          refreshKey={refreshKey}
          setFetchedTrades={handleFetchedTrades}
          onToast={showToast}
        />
      </div>
    </div>
  );
}